import { useEffect, useRef, useState } from "react";
import type { Station, NowPlaying } from "../lib/radio";
import { ChannelMark } from "./ChannelMark";

type Props = {
  station: Station | null;
  np: NowPlaying | null;
  playing: boolean;
  onTogglePlay: () => void;
  onOpenPicker: () => void;
  onNext: () => void;
  onPrev: () => void;
  /** Forwarded ref so parent can mount an actual <audio> tag and control it. */
  audioRef: React.RefObject<HTMLAudioElement>;
};

export function Player({ station, np, playing, onTogglePlay, onOpenPicker, onNext, onPrev, audioRef }: Props) {
  const [coverLoading, setCoverLoading] = useState(false);
  const lastCoverRef = useRef<string | null>(null);

  // Volume is handled by the head unit's hardware buttons (they scale the
  // OS output), so we keep the <audio> at full level and show no slider.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = 1;
  }, [audioRef, playing]);

  // cover swap detection
  const coverUrl = np?.albumartUrl || station?.art || null;
  useEffect(() => {
    if (coverUrl && coverUrl !== lastCoverRef.current) {
      setCoverLoading(true);
      const img = new Image();
      img.onload = () => { setCoverLoading(false); lastCoverRef.current = coverUrl; };
      img.onerror = () => { setCoverLoading(false); lastCoverRef.current = coverUrl; };
      img.src = coverUrl;
    }
  }, [coverUrl]);

  const title = np?.title;
  const artist = np?.artist;
  const stationName = station?.name || "Select a station";

  // The whole panel is a big tap target that opens the station picker —
  // easier to hit while driving. The transport buttons sit on top and
  // stop propagation so play/prev/next don't also open the picker.
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };

  return (
    <div
      className="player"
      role="button"
      tabIndex={0}
      onClick={onOpenPicker}
      aria-label="Vælg kanal"
    >
      <div className={`cover ${playing ? "ambient" : ""}`}>
        {coverUrl ? (
          <img src={coverUrl} alt="" className={coverLoading ? "fade-out" : ""} />
        ) : (
          <div className="cover-fallback">{stationName}</div>
        )}
      </div>

      <div className="player-info">
        <div className="station-line">
          <span className={playing ? "live-dot" : ""} style={!playing ? { width: 8, height: 8, borderRadius: 8, background: "var(--text-3)" } : undefined} />
          <span>{playing ? "Live · " : ""}{stationName}</span>
          <ChevronRight />
        </div>

        <div>
          <div className="track-title">{title || (station?.tagline ?? "Press play to begin")}</div>
          <div className="track-artist">{artist || (station ? "—" : "Pick a station to start")}</div>
        </div>

        <div className="player-controls">
          <div className="transport">
            <button className="tbtn" onClick={stop(onPrev)} aria-label="Previous station">
              <PrevIcon />
            </button>
            <button className="tbtn play" onClick={stop(onTogglePlay)} aria-label={playing ? "Pause" : "Play"}>
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button className="tbtn" onClick={stop(onNext)} aria-label="Next station">
              <NextIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 5.5v13c0 .8.9 1.3 1.6.9l10.5-6.5a1.1 1.1 0 0 0 0-1.8L8.6 4.6A1.1 1.1 0 0 0 7 5.5z" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1.2" />
      <rect x="14" y="5" width="4" height="14" rx="1.2" />
    </svg>
  );
}
function PrevIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 5v14a1 1 0 0 0 2 0v-6.2l9 5.7a1 1 0 0 0 1.5-.9V6.4a1 1 0 0 0-1.5-.9L9 11.2V5a1 1 0 0 0-2 0z" />
    </svg>
  );
}
function NextIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17 5v14a1 1 0 0 1-2 0v-6.2l-9 5.7a1 1 0 0 1-1.5-.9V6.4a1 1 0 0 1 1.5-.9L15 11.2V5a1 1 0 0 1 2 0z" />
    </svg>
  );
}
function ChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
